/**
 * Filter Utilities
 *
 * Functions to apply filters to GeoJSON features.
 * Supports 4 modes: exact, pattern, comparison, ranking
 */

import type { Feature, FeatureCollection, Geometry, GeoJsonProperties } from "geojson";
import type { FilterRule } from "@/stores/useStudioStore";

type PatternMatcher = (value: string) => boolean;

/**
 * Extract string values from a property value (handles arrays and primitives)
 */
function extractStringValues(value: unknown): string[] {
  if (value === null || value === undefined) {
    return [];
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => (item === null || item === undefined ? "" : String(item)))
      .filter((str) => str.length > 0);
  }
  const str = String(value);
  return str.length > 0 ? [str] : [];
}

/**
 * Parse a string value as a number, handling common formatting
 * Removes commas and spaces before parsing
 * Returns null if the value cannot be parsed as a number
 */
function parseNumeric(value: string): number | null {
  const cleaned = value.replace(/[,\s]/g, "");
  const parsed = Number(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Compare two numeric values
 * Handles formatted numbers (with commas/spaces) correctly
 */
function compareNumeric(
  value: string,
  compareValue: string,
  operator: string
): boolean {
  const a = parseNumeric(value);
  const b = parseNumeric(compareValue);

  // If either value isn't numeric, fall back to string comparison
  if (a === null || b === null) {
    switch (operator) {
      case ">":
        return value > compareValue;
      case "<":
        return value < compareValue;
      case ">=":
        return value >= compareValue;
      case "<=":
        return value <= compareValue;
      case "=":
        return value === compareValue;
      case "!=":
        return value !== compareValue;
      default:
        return false;
    }
  }

  // Numeric comparison
  switch (operator) {
    case ">":
      return a > b;
    case "<":
      return a < b;
    case ">=":
      return a >= b;
    case "<=":
      return a <= b;
    case "=":
      return a === b;
    case "!=":
      return a !== b;
    default:
      return false;
  }
}

/**
 * Escape regex special characters
 */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Create a pattern matcher from a pattern string
 * - Supports /regex/ format
 * - Supports X as digit wildcard (e.g., X1X matches 212, 313, etc.)
 */
function createPatternMatcher(pattern: string): PatternMatcher | null {
  const trimmed = pattern.trim();
  if (!trimmed) {
    return null;
  }

  // Regex format: /expression/
  if (trimmed.startsWith("/") && trimmed.endsWith("/") && trimmed.length > 2) {
    try {
      const expression = trimmed.slice(1, -1);
      const regex = new RegExp(expression);
      return (value) => regex.test(value);
    } catch {
      return null;
    }
  }

  // X wildcard format: X = any digit
  const escaped = escapeRegExp(trimmed);
  const expanded = escaped.replace(/X/gi, "[0-9]");
  try {
    const regex = new RegExp(`^${expanded}$`);
    return (value) => regex.test(value);
  } catch {
    return null;
  }
}

/**
 * Evaluate if a feature's property value matches a filter
 */
function evaluateFilter(
  feature: Feature<Geometry, GeoJsonProperties>,
  filter: FilterRule
): boolean {
  const rawValue = feature.properties?.[filter.propertyKey];
  const values = extractStringValues(rawValue);

  if (values.length === 0) {
    return false;
  }

  // Comparison mode
  if (filter.mode === "comparison") {
    if (filter.values.length === 0 || !filter.operator) {
      return true;
    }
    const compareValue = filter.values[0] ?? "";
    if (!compareValue.trim()) {
      return true;
    }
    return values.some((value) =>
      compareNumeric(value, compareValue, filter.operator!)
    );
  }

  // Pattern mode
  if (filter.mode === "pattern") {
    if (filter.values.length === 0) {
      return true;
    }
    const matchers = filter.values
      .map(createPatternMatcher)
      .filter((m): m is PatternMatcher => m !== null);
    if (matchers.length === 0) {
      return true;
    }
    return values.some((value) => matchers.some((matcher) => matcher(value)));
  }

  // Exact mode
  if (filter.mode === "exact") {
    if (filter.values.length === 0) {
      return true;
    }
    const allowed = new Set(filter.values);
    return values.some((value) => allowed.has(value));
  }

  // Ranking mode - handled separately
  return true;
}

/**
 * Apply ranking filter to features
 */
function applyRankingFilter(
  features: Feature<Geometry, GeoJsonProperties>[],
  filter: FilterRule
): Feature<Geometry, GeoJsonProperties>[] {
  if (!filter.rankingDirection || !filter.rankingCount) {
    return features;
  }

  // Collect all unique values with their features
  const valueMap = new Map<string, Feature<Geometry, GeoJsonProperties>[]>();
  features.forEach((feature) => {
    const rawValue = feature.properties?.[filter.propertyKey];
    const values = extractStringValues(rawValue);
    if (values.length > 0) {
      const valueStr = values[0] || "";
      const existing = valueMap.get(valueStr) ?? [];
      existing.push(feature);
      valueMap.set(valueStr, existing);
    }
  });

  // Sort values (numeric if possible, string otherwise)
  const sortedValues = Array.from(valueMap.keys()).sort((a, b) => {
    const numA = parseNumeric(a);
    const numB = parseNumeric(b);
    if (numA !== null && numB !== null) {
      return filter.rankingDirection === "top" ? numB - numA : numA - numB;
    }
    return filter.rankingDirection === "top"
      ? b.localeCompare(a, undefined, { numeric: true })
      : a.localeCompare(b, undefined, { numeric: true });
  });

  // Take top/bottom N values
  const selectedValues = new Set(sortedValues.slice(0, filter.rankingCount));

  // Return all features that have one of the selected values
  return features.filter((feature) => {
    const rawValue = feature.properties?.[filter.propertyKey];
    const values = extractStringValues(rawValue);
    return values.length > 0 && selectedValues.has(values[0] || "");
  });
}

/**
 * Apply all filters to a feature collection
 */
export function applyFilters(
  collection: FeatureCollection<Geometry, GeoJsonProperties>,
  filters: FilterRule[]
): FeatureCollection<Geometry, GeoJsonProperties> {
  if (filters.length === 0) {
    return collection;
  }

  // Separate ranking from non-ranking filters
  const nonRankingFilters = filters.filter((f) => f.mode !== "ranking");
  const rankingFilters = filters.filter((f) => f.mode === "ranking");

  // Apply non-ranking filters first
  let filteredFeatures = collection.features;
  for (const filter of nonRankingFilters) {
    filteredFeatures = filteredFeatures.filter((feature) =>
      evaluateFilter(feature, filter)
    );
  }

  // Apply ranking filters last (they need to see all remaining features)
  for (const filter of rankingFilters) {
    filteredFeatures = applyRankingFilter(filteredFeatures, filter);
  }

  return {
    ...collection,
    features: filteredFeatures,
  };
}

/**
 * Count features after applying filters
 */
export function countFilteredFeatures(
  collection: FeatureCollection<Geometry, GeoJsonProperties> | null,
  filters: FilterRule[]
): number {
  if (!collection) {
    return 0;
  }
  if (filters.length === 0) {
    return collection.features.length;
  }
  const filtered = applyFilters(collection, filters);
  return filtered.features.length;
}

/**
 * Evaluate if properties pass all filters
 * Used for checking individual features in knowledge map
 */
export function evaluateFilters(
  properties: GeoJsonProperties,
  filters: FilterRule[]
): boolean {
  if (filters.length === 0) {
    return true;
  }

  // Create a temporary feature to use existing evaluateFilter logic
  const tempFeature: Feature<Geometry, GeoJsonProperties> = {
    type: "Feature",
    properties,
    geometry: { type: "Point", coordinates: [0, 0] }, // Dummy geometry
  };

  // Only evaluate non-ranking filters (ranking needs full feature collection)
  const nonRankingFilters = filters.filter((f) => f.mode !== "ranking");

  return nonRankingFilters.every((filter) => evaluateFilter(tempFeature, filter));
}

/**
 * Filter array values within features based on filters.
 *
 * This is critical for quiz correctness: when a feature has array properties
 * (e.g., AreaCodes: ["404", "621", "583"]) and filters are applied,
 * only the array values that match the filter should generate quiz prompts.
 *
 * Example:
 * - Feature: Atlanta with AreaCodes = ["404", "621", "583"]
 * - Filter: Pattern "X0X" (has 0 in middle)
 * - Result: AreaCodes = ["404"] (621 and 583 are removed)
 *
 * @param collection - Feature collection to process
 * @param filters - Filter rules to apply to array values
 * @param promptKeys - Property keys used for quiz prompts (only filter these)
 * @returns Collection with filtered array values
 */
export function filterArrayValues(
  collection: FeatureCollection<Geometry, GeoJsonProperties>,
  filters: FilterRule[],
  promptKeys: string[]
): FeatureCollection<Geometry, GeoJsonProperties> {
  if (filters.length === 0) {
    return collection;
  }

  // Only consider non-ranking filters (ranking needs full collection context)
  const nonRankingFilters = filters.filter((f) => f.mode !== "ranking");
  if (nonRankingFilters.length === 0) {
    return collection;
  }

  // Process each feature
  const processedFeatures = collection.features.map((feature) => {
    // Only modify features that have properties
    if (!feature.properties) {
      return feature;
    }

    const newProperties = { ...feature.properties };
    let modified = false;

    // For each prompt key, filter its array values
    for (const key of promptKeys) {
      const rawValue = feature.properties[key];

      // Skip if not an array or doesn't exist
      if (!Array.isArray(rawValue)) {
        continue;
      }

      // Get filters that apply to this property key
      const relevantFilters = nonRankingFilters.filter((f) => f.propertyKey === key);
      if (relevantFilters.length === 0) {
        continue;
      }

      // Filter each value in the array
      const filteredValues = rawValue.filter((value) => {
        const valueStr = value === null || value === undefined ? "" : String(value);
        if (!valueStr) {
          return false;
        }

        // Check if this value passes all relevant filters
        return relevantFilters.every((filter) => {
          // Comparison mode
          if (filter.mode === "comparison") {
            if (filter.values.length === 0 || !filter.operator) {
              return true;
            }
            const compareValue = filter.values[0] ?? "";
            if (!compareValue.trim()) {
              return true;
            }
            return compareNumeric(valueStr, compareValue, filter.operator);
          }

          // Pattern mode
          if (filter.mode === "pattern") {
            if (filter.values.length === 0) {
              return true;
            }
            const matchers = filter.values
              .map(createPatternMatcher)
              .filter((m): m is PatternMatcher => m !== null);
            if (matchers.length === 0) {
              return true;
            }
            return matchers.some((matcher) => matcher(valueStr));
          }

          // Exact mode
          if (filter.mode === "exact") {
            if (filter.values.length === 0) {
              return true;
            }
            const allowed = new Set(filter.values);
            return allowed.has(valueStr);
          }

          return true;
        });
      });

      // Only update if values were filtered out
      if (filteredValues.length < rawValue.length) {
        newProperties[key] = filteredValues;
        modified = true;
      }
    }

    // Return modified feature if changed, otherwise original
    return modified
      ? { ...feature, properties: newProperties }
      : feature;
  });

  return {
    ...collection,
    features: processedFeatures,
  };
}
